import type { TimelineItem } from "@/models/legislation/Timeline";

const ISO_DATE_PATTERN = /^(\d{4})-(\d{1,2})-(\d{1,2})$/;
const DANISH_DATE_PATTERN = /^(\d{1,2})[/-](\d{1,2})[/-](\d{4})$/;

const pad = (value: number): string => String(value).padStart(2, "0");

const buildDate = (year: number, month: number, day: number): Date | null => {
  const date = new Date(year, month - 1, day);
  // Date silently rolls over impossible values (31/02 → 03/03); reject them.
  if (
    date.getFullYear() !== year ||
    date.getMonth() !== month - 1 ||
    date.getDate() !== day
  ) {
    return null;
  }
  return date;
};

/**
 * Parse a timeline signature date. Accepts the canonical ISO `YYYY-MM-DD` that
 * `normalizeTimelineDates` produces as well as the Danish `DD/MM/YYYY` /
 * `DD-MM-YYYY` shape stored in `timeline_gold`, so values that reach the app
 * without crossing the query boundary still parse.
 */
export const parseSignatureDate = (dateStr?: string): Date | null => {
  const datePart = dateStr?.trim().split(/[T ]/)[0];
  if (!datePart) return null;

  const iso = ISO_DATE_PATTERN.exec(datePart);
  if (iso) return buildDate(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const danish = DANISH_DATE_PATTERN.exec(datePart);
  if (danish) {
    return buildDate(Number(danish[3]), Number(danish[2]), Number(danish[1]));
  }

  return null;
};

/** Display format for Danish legal dates: `DD/MM/YYYY`. */
export const formatDanishDate = (date: Date): string =>
  `${pad(date.getDate())}/${pad(date.getMonth() + 1)}/${date.getFullYear()}`;

/** Local-calendar ISO `YYYY-MM-DD` — never `toISOString`, which shifts the day. */
export const toIsoDate = (date: Date): string =>
  `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;

/**
 * Rewrite every `signatureDate` in a timeline tree to ISO `YYYY-MM-DD`.
 *
 * `timeline_gold` stores Danish `DD/MM/YYYY`, while every other date the API
 * returns (`resolvedAsOf`, `approvedAt`, `publishedOn`) is ISO. Mixing the two
 * in a string comparison yields a plausible but wrong answer, so timelines are
 * normalised on the way in and the whole app compares and sorts one format.
 * Values that do not parse are passed through untouched.
 */
export const normalizeTimelineDates = (
  items: TimelineItem[] | null | undefined
): TimelineItem[] =>
  (items ?? []).map((item) => {
    const parsed = parseSignatureDate(item.signatureDate);
    return {
      ...item,
      ...(parsed ? { signatureDate: toIsoDate(parsed) } : {}),
      ...(item.children
        ? { children: normalizeTimelineDates(item.children) }
        : {}),
    };
  });
